import classNames from 'classnames'
import { FormikProps } from 'formik'
import { useState } from 'react'
import { NumericFormat } from 'react-number-format'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../constants'
import { getCurrencySymbol } from '../../helpers'
import { useAppSelector } from '../../hooks'
import { useModalControl } from '../../hooks/useModalControl'
import { useGetAllVendorsQuery } from '../../redux-toolkits/vendors/vendors.slice'
import { FulfilmentCentre } from '../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import {
  CreateProductValues,
  Product,
  ProductType,
} from '../../redux-toolkits/products/products.type'
import { AllVendorsSingleVendor } from '../../redux-toolkits/vendors/vendors.types'
import Icon from '../icon'
import { Checkbox } from '../inputs/Checkbox'
import ComboBox from '../inputs/combobox'
import { EditInput } from '../inputs/editInput'
import { TextInput } from '../inputs/textInput'
import { UpdateItemStockModal } from '../modal/app_modals/updateItemStockmodal'
import { HeaderSubheader } from '../typography/HeaderSubheader'
import { ManageProductKeywords } from './manageProductKeywords'
import { mapCreatorLocationsToForm } from './product-location-mappers'

export const ManageProductDetails = ({
  formik,
  className,
  typeTitle,
  id,
  product,
  productType,
  variantComboQuantity,
  inventoryQuantity,
  usesFulfilmentCentreInventory,
  fulfilmentCentres,
  onCreatorSelected,
  onInventoryAdjusted,
  refetchProductDetails,
}: {
  formik: FormikProps<CreateProductValues>
  className?: string
  typeTitle: string
  id?: string
  product?: Product
  productType: ProductType
  variantComboQuantity?: number
  inventoryQuantity?: number
  usesFulfilmentCentreInventory: boolean
  fulfilmentCentres: FulfilmentCentre[]
  onCreatorSelected: (vendorId: string) => void
  onInventoryAdjusted: (adjustment: {
    fulfilmentCentreId?: string
    quantityDelta: number
  }) => void
  refetchProductDetails: () => void
}) => {
  const [editIndex, setEditIndex] = useState<number>()
  const [vendorSearch, setVendorSearch] = useState<string>()

  const { selectedFulfilmentCenters } = useAppSelector((state) => state.auth)

  const { modal, modalOpen, handleModalClose, handleModalOpen, setModalOpen } =
    useModalControl()

  const { values, errors, setFieldValue, handleBlur, handleChange, touched } =
    formik

  const { data: vendors, isFetching: fetchingVendors } = useGetAllVendorsQuery(
    {
      page: `1`,
      limit: NUMBER_OF_ITEMS_PER_PAGE.toString(),
      search: vendorSearch === '' ? undefined : vendorSearch,
      fulfillmentcenterId:
        selectedFulfilmentCenters.map((c) => c._id).join(',') || undefined,
    },
    { refetchOnMountOrArgChange: true },
  )

  return (
    <>
      <div className={classNames('flex flex-col gap-6', className)}>
        <HeaderSubheader
          title={`${typeTitle} Details`}
          text={`Enter basic ${typeTitle} details and information.`}
        />

        <div className="grid grid-flow-row grid-cols-[repeat(auto-fit,_minmax(302px,1fr))] gap-4">
          <div className="col-span-full">
            <EditInput
              title={`${typeTitle} name`}
              value={values.name}
              onChange={handleChange}
              type="text"
              editing={editIndex === 1}
              onEditClick={() => {
                setEditIndex(1)
              }}
              placeholder={`${typeTitle} name`}
              onEditSave={() => {
                setEditIndex(undefined)
              }}
              onCancel={() => {
                setFieldValue('name', product?.name || '')
              }}
              inputName="name"
              errors={errors.name && touched.name ? errors.name : ''}
              mode={id ? 'edit' : 'new'}
            />
          </div>

          <ManageProductKeywords className="col-span-full" formik={formik} />

          <div className="col-span-full">
            <EditInput
              title={`${typeTitle} description`}
              value={values.description}
              onChange={handleChange}
              type="textarea"
              editing={editIndex === 2}
              onEditClick={() => {
                setEditIndex(2)
              }}
              placeholder={`${typeTitle} description`}
              onEditSave={() => {
                setEditIndex(undefined)
              }}
              onCancel={() => {
                setFieldValue('description', product?.description || '')
              }}
              inputName="description"
              errors={
                errors.description && touched.description
                  ? errors.description
                  : ''
              }
              mode={id ? 'edit' : 'new'}
            />
          </div>

          <ComboBox
            type="text"
            name="vendor.name"
            value={values.vendor.name}
            onChange={handleChange}
            onBlur={handleBlur}
            hasIcon={false}
            placeholder="Search creator"
            errors={
              touched.vendor?.name && errors.vendor?.name
                ? errors.vendor?.name
                : ''
            }
            menuItems={
              vendors?.data.vendors
                ?.filter((v) => v.status === 'active')
                ?.map((vendor) => {
                  return {
                    name: vendor.businessName,
                    value: {
                      name: vendor.businessName,
                      value: vendor,
                    },
                  }
                }) ?? []
            }
            onSelectOption={(selected: {
              name: string
              value: AllVendorsSingleVendor
            }) => {
              const creatorLocations = mapCreatorLocationsToForm(selected.value)

              setFieldValue('vendor', {
                _id: selected.value._id,
                name: selected.name,
              })
              onCreatorSelected(selected.value._id)
              setVendorSearch(undefined)
              setFieldValue('creatorDeliveryLocation', creatorLocations)

              if (values.availabilityPolicy === 'CUSTOM') {
                setFieldValue('deliveryLocation', creatorLocations)
              }
            }}
            onSearch={(val) => {
              setVendorSearch(val)
            }}
            loading={fetchingVendors}
            immediate
            position="bottom start"
            menuClassName="!max-h-[250px] shadow-md"
            className="w-full"
            title="Creator"
            isDropdown
          />

          <NumericFormat
            suffix={undefined}
            prefix={getCurrencySymbol('NGN')}
            decimalScale={2}
            thousandSeparator
            customInput={TextInput}
            type="text"
            name="price"
            title={`${typeTitle} Price`}
            placeholder="Price"
            value={values.price}
            onBlur={handleBlur}
            onChange={handleChange}
            errors={errors.price && touched.price ? errors.price : ''}
            hasIcon={false}
            readOnly={id !== undefined}
            showAsReadOnly
            className=""
            disabled={id !== undefined}
            extra={
              id !== undefined && productType !== 'bundle' ? (
                <button
                  type="button"
                  className="w-fit"
                  onClick={() => {
                    handleModalOpen('updateItemPriceModal')
                  }}
                >
                  <Icon id="edit" width={16} height={16} />
                </button>
              ) : undefined
            }
          />

          <TextInput
            type="text"
            name="quantityInStock"
            title="Quantity"
            placeholder="Quantity in stock"
            value={
              inventoryQuantity !== undefined
                ? String(inventoryQuantity)
                : values.isVariant
                  ? `${variantComboQuantity}`
                  : values.quantityInStock
            }
            onBlur={handleBlur}
            onChange={handleChange}
            errors={
              errors.quantityInStock && touched.quantityInStock
                ? errors.quantityInStock
                : ''
            }
            hasIcon={false}
            readOnly={
              id !== undefined ||
              values.isVariant ||
              usesFulfilmentCentreInventory
            }
            showAsReadOnly
            className=""
            extra={
              id !== undefined && !values.isVariant ? (
                <button
                  type="button"
                  className="w-fit"
                  onClick={() => {
                    handleModalOpen('updateItemStockModal')
                  }}
                >
                  <Icon id="edit" width={16} height={16} />
                </button>
              ) : undefined
            }
          />

          <div className="col-span-full flex flex-col gap-4 justify-center">
            <Checkbox
              checkStatus={values.lowStockAlert}
              text="Enable low stock notification"
              handleChange={(status) => {
                setFieldValue('lowStockAlert', status)
              }}
            />

            {values.lowStockAlert && (
              <TextInput
                type="text"
                name="lowStockQuantity"
                placeholder="Low stock quantity"
                value={values.lowStockQuantity}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={
                  errors.lowStockQuantity && touched.lowStockQuantity
                    ? errors.lowStockQuantity
                    : ''
                }
                hasIcon={false}
                className="max-w-[302px]"
              />
            )}
          </div>

          <div className="col-span-full flex flex-col gap-4 justify-center">
            <Checkbox
              checkStatus={values.isMarkupEnabled}
              text="Enable markup percentage"
              handleChange={(status) => {
                setFieldValue('isMarkupEnabled', status)
              }}
            />

            {values.isMarkupEnabled && (
              <TextInput
                type="text"
                name="markupValue"
                placeholder="Markup value"
                value={values.markupValue ?? ''}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={
                  errors.markupValue && touched.markupValue
                    ? errors.markupValue
                    : ''
                }
                hasIcon={false}
                className="max-w-[302px]"
              />
            )}
          </div>

          <div className="col-span-full flex flex-col gap-4 justify-center">
            <Checkbox
              checkStatus={values.instantDeliveryEnabled}
              text="Enable instant delivery"
              handleChange={(status) => {
                setFieldValue('instantDeliveryEnabled', status)
              }}
            />
          </div>
        </div>
      </div>

      <UpdateItemStockModal
        open={
          modalOpen &&
          ['updateItemStockModal', 'updateItemPriceModal'].includes(modal)
        }
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={(adjustment) => {
          handleModalClose()
          refetchProductDetails()
          if (adjustment) onInventoryAdjusted(adjustment)
        }}
        product={product}
        fulfilmentCentres={fulfilmentCentres}
        type={modal === 'updateItemStockModal' ? 'quantity' : 'price'}
      />
    </>
  )
}
